import {useState, useEffect} from 'react';
import type {ScreenBreakpoints} from '@Libraries/responsiveness-library';

const getMediaQueryForBreakpoint = (breakpointWidth: ScreenBreakpoints): MediaQueryList => {
  return window.matchMedia(`(max-width: ${breakpointWidth}px)`);
};

/**
 * True while the viewport is at or below the given breakpoint (`max-width: Npx` semantics, i.e. `<=`).
 * Built on `matchMedia`'s `change` event, which fires only on breakpoint crossings — unlike a `resize`
 * listener, an idle consumer costs nothing while the user drags the window.
 * @author Moeed Ahmad <moeeed@250mils.com>
 */

export const useIsSmallerThanBreakpointWidth = (breakpointWidth: ScreenBreakpoints): boolean => {
  const [isSmaller, setIsSmaller] = useState((): boolean => {
    return getMediaQueryForBreakpoint(breakpointWidth).matches;
  });

  useEffect(() => {
    const mediaQuery = getMediaQueryForBreakpoint(breakpointWidth);
    setIsSmaller(mediaQuery.matches);

    const onMediaQueryChange = (event: MediaQueryListEvent): void => {
      setIsSmaller(event.matches);
    };

    mediaQuery.addEventListener('change', onMediaQueryChange);
    return (): void => {
      mediaQuery.removeEventListener('change', onMediaQueryChange);
    };
  }, [breakpointWidth]);

  return isSmaller;
};
